import React, { ChangeEvent, FC, useMemo, useState } from "react";
import { Collapse, FormControl, FormGroup, FormLabel, TextField } from "@material-ui/core";
import { ExpandLess, ExpandMore } from "@material-ui/icons";

import CheckBox from "../../../../../components/elements/CheckBox";
import classes from "../../RoleTitleSelectionSlide.module.scss";
import { CookieSet, RoleSet, RoleTitleGroup as RoleTitleGroupType } from "../../../../../types/campaignTypes";
import { collectCookieSets, walkRoleSets } from "../../../../redesign/RolesTitlesView/roleTitleSetUtils";

type RoleTitleGrouptProps = {
  checkBoxes: Record<string, boolean>;
  roleTitleGroup: RoleTitleGroupType;
  handleChange: (cookieSet: CookieSet[]) => (event: ChangeEvent<{}>) => void;
  handleSelectAll: (roleType: RoleTitleGroupType) => (event: ChangeEvent<{}>) => void;
  /** Optional: cascade select into cookie state by role keys (preferred for hierarchy). */
  onToggleRole?: (roleTypeId: string, role: RoleSet) => void;
};

const filterRoleTree = (roles: RoleSet[], term: string): RoleSet[] => {
  if (!term.trim()) return roles;
  const lower = term.toLowerCase();
  const walk = (nodes: RoleSet[]): RoleSet[] => {
    const result: RoleSet[] = [];
    for (const node of nodes) {
      const children = node.children ? walk(node.children) : [];
      const selfMatch = node.role.toLowerCase().includes(lower);
      if (selfMatch || children.length > 0) {
        result.push({ ...node, children: children.length ? children : undefined });
      }
    }
    return result;
  };
  return walk(roles);
};

const RoleTreeRows: FC<{
  roles: RoleSet[];
  roleTypeId: string;
  checkBoxes: Record<string, boolean>;
  handleChange: (cookieSet: CookieSet[]) => (event: ChangeEvent<{}>) => void;
  onToggleRole?: (roleTypeId: string, role: RoleSet) => void;
  depth?: number;
  forceExpand?: boolean;
}> = ({ roles, roleTypeId, checkBoxes, handleChange, onToggleRole, depth = 0, forceExpand = false }) => (
  <>
    {roles.map((roleSet) => (
      <RoleTreeNode
        key={roleSet.roleId}
        roleSet={roleSet}
        roleTypeId={roleTypeId}
        checkBoxes={checkBoxes}
        handleChange={handleChange}
        onToggleRole={onToggleRole}
        depth={depth}
        forceExpand={forceExpand}
      />
    ))}
  </>
);

const RoleTreeNode: FC<{
  roleSet: RoleSet;
  roleTypeId: string;
  checkBoxes: Record<string, boolean>;
  handleChange: (cookieSet: CookieSet[]) => (event: ChangeEvent<{}>) => void;
  onToggleRole?: (roleTypeId: string, role: RoleSet) => void;
  depth: number;
  forceExpand: boolean;
}> = ({ roleSet, roleTypeId, checkBoxes, handleChange, onToggleRole, depth, forceExpand }) => {
  const hasChildren = !!roleSet.children?.length;
  const [expanded, setExpanded] = useState(false);
  const open = forceExpand || expanded;
  const key = `${roleTypeId}:${roleSet.roleId}`;

  return (
    <div style={{ paddingLeft: depth * 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
        {hasChildren ? (
          <button
            type="button"
            aria-label={open ? "Collapse" : "Expand"}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setExpanded((p) => !p);
            }}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              padding: 0,
              display: "inline-flex",
              width: 24,
              height: 24,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </button>
        ) : (
          <span style={{ width: 24, height: 24, display: "inline-block" }} />
        )}
        <CheckBox
          checked={!!checkBoxes[key]}
          name={key}
          label={roleSet.role}
          className={classes.checkbox}
          onChange={onToggleRole ? () => onToggleRole(roleTypeId, roleSet) : handleChange(collectCookieSets(roleSet))}
        />
      </div>
      {hasChildren ? (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <RoleTreeRows
            roles={roleSet.children!}
            roleTypeId={roleTypeId}
            checkBoxes={checkBoxes}
            handleChange={handleChange}
            onToggleRole={onToggleRole}
            depth={depth + 1}
            forceExpand={forceExpand}
          />
        </Collapse>
      ) : null}
    </div>
  );
};

const RoleTitleGroup: FC<RoleTitleGrouptProps> = ({
  checkBoxes,
  roleTitleGroup,
  handleChange,
  handleSelectAll,
  onToggleRole,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const handleToggle = () => setOpen((prev) => !prev);

  const filteredRoles = useMemo(() => filterRoleTree(roleTitleGroup.roleSet, search), [search, roleTitleGroup.roleSet]);

  let allSelected = true;
  let anyRole = false;
  walkRoleSets(roleTitleGroup.roleSet, (rs) => {
    anyRole = true;
    if (!checkBoxes[`${roleTitleGroup.roleTypeId}:${rs.roleId}`]) allSelected = false;
  });
  if (!anyRole) allSelected = false;

  return (
    <div className={classes.roleTitleGroupContainer}>
      <FormControl component="fieldset" fullWidth>
        <div className={classes.roleTitleHeader}>
          <FormLabel
            component="legend"
            className={classes.roleTypeLabel}
            onClick={handleToggle}
            style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
          >
            {open ? <ExpandLess /> : <ExpandMore />}
            <span style={{ marginLeft: 8 }}>{roleTitleGroup.roleType}</span>
          </FormLabel>
        </div>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <div className={classes.searchWrapper}>
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search roles..."
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={classes.searchInput}
            />
          </div>

          <FormGroup>
            <CheckBox
              checked={allSelected}
              onChange={handleSelectAll(roleTitleGroup)}
              name={`${roleTitleGroup.roleType}_ALL`}
              label="Select all"
              className={classes.checkbox}
            />
            <RoleTreeRows
              roles={filteredRoles}
              roleTypeId={roleTitleGroup.roleTypeId}
              checkBoxes={checkBoxes}
              handleChange={handleChange}
              onToggleRole={onToggleRole}
              forceExpand={!!search.trim()}
            />
          </FormGroup>
        </Collapse>
      </FormControl>
    </div>
  );
};

export default RoleTitleGroup;
